/**
 * Interactive TUI for managing AI assets.
 *
 * Tabs: [Assets] [Sources] [Libraries] [Pending] [Sync]
 *
 *   - Assets    — browse and queue install/remove for assets, grouped in a
 *                 `origin → publisher → domain → asset` tree. `origin` is the feed
 *                 the asset came from (Library / Sources / Skaile Factory Assets
 *                 / Store), the outermost collapsible band so it's clear where
 *                 each skill comes from.
 *                 `domain` is the sidecar `DOMAIN.md` grouping (display-only).
 *   - Sources   — third-party github repos registered via `skaile source add`,
 *                 shown as a `Sources → org → repo → domain → asset` tree (the
 *                 source-origin slice of the Assets feed, with an extra github-org
 *                 band). Domains default *collapsed*; install/remove works the
 *                 same as the Assets tab. CRUD stays in `skaile source …`.
 *   - Libraries — user authoring places registered via `skaile library init`.
 *   - Pending   — staged adds/removes (rich rendering deferred to AF-LIB-TUI-RICH).
 *   - Sync      — bulk sync sources + libraries (deferred to AF-LIB-TUI-RICH).
 *
 * Keys: ↑↓/jk navigate, ←/→ collapse/expand, space/+/- select, a/Enter apply,
 *       d domain info, 1/2/3 filter to one origin · 0 clear, s sync, i info,
 *       Tab switch, q/Esc quit
 *
 * NOTE: rich rendering (status glyphs, behind/ahead counters, in-line sync) for
 * the Sources/Libraries/Pending/Sync tabs is deferred to AF-LIB-TUI-RICH (see
 * workspaces/issues.md). The current implementation is a minimal scaffold.
 */

import path from "node:path";
import { AssetManager } from "@skaile/workspaces/asset-manager";
import {
  assetRefToStr,
  type CatalogDomain,
  type CatalogEntry,
  type DomainRelation,
} from "@skaile/workspaces/core";
import { Command } from "commander";
import pc from "picocolors";
import type { AssetOrigin, OriginEntry } from "../asset-feeds.ts";
import {
  bareSoftDepName,
  fitWidth,
  formatSoftDepAddPreview,
  formatSoftDepRemovePreview,
  kindColor,
  kindColorPad,
  S,
} from "../helpers.ts";

// ── Types ────────────────────────────────────────────────────────────────────

/**
 * A single row in the manage TUI asset list: an origin header, a publisher
 * header, a domain header, a domain info line, or an individual asset entry.
 *
 * The tree is `origin → publisher → domain → asset`. `origin` (the feed the
 * asset was gathered from — store / source / library / factory) is the outermost
 * band and is carried on **every** row, because the same group can appear
 * under more than one origin and must not collapse together. `source` holds the
 * **second-level group** — the publisher namespace for most origins, but the
 * source's short repo name under the Sources band (see {@link OriginEntry.group});
 * it is set on header rows **and** asset rows so grouping stays consistent.
 * `domainSlug` is the
 * navigational group from a sidecar `DOMAIN.md` (`""` = the synthetic "no
 * domain" group). A `repo` (`"<org>/<repo>"`) is no longer a grouping axis —
 * it rides on the asset row as a dim provenance badge only.
 *
 * A `domain-header` is **navigational**: it collapses/expands and toggles its
 * info region, but never bulk-installs (unlike origin/publisher headers).
 *
 * @docLink cli/commands/manage#make-manage-command
 */
export interface AssetRow {
  type:
    | "origin-header"
    | "publisher-header"
    | "domain-header"
    | "domain-info"
    | "asset"
    // Sources-tab tree only: a github-org band ("skaile-ai") and the repo band
    // ("ai-assets-skaileup") under a single static "Sources" root.
    | "source-root"
    | "org-header"
    | "repo-header";
  origin?: AssetOrigin; // present on all row types (the outer grouping band)
  source?: string; // second-level group key (publisher, or source/repo name under Sources)
  org?: string; // github org (Sources tree); set on every source-origin asset row + its headers
  domainSlug?: string; // domain-header / domain-info: group slug ("" = no domain); asset: its domain
  repo?: string; // asset: "<org>/<repo>" provenance badge (not a collapse axis)
  entry?: CatalogEntry;
  installed?: boolean;
  underDomain?: boolean; // asset: rendered under a domain-header (drives indent + relation glyph)
  seq?: number; // asset: 1-based step within a `sequence` domain
}

/** Display metadata + render order for the asset origins. */
const ORIGIN_META: Record<AssetOrigin, { icon: string; label: string }> = {
  library: { icon: "📚", label: "Library" },
  source: { icon: "🔗", label: "Sources" },
  factory: { icon: "🏭", label: "Skaile Factory Assets" },
  store: { icon: "📦", label: "Store" },
};

/**
 * Render order, local-first: your own authoring places, then github sources you
 * added, then the bundled first-party factory assets, then the (potentially
 * large, remote) store last. {@link loadAssets} default-collapses `store` so
 * local assets stay front-and-centre on open.
 */
const ORIGIN_ORDER: AssetOrigin[] = ["library", "source", "factory", "store"];

/** Collapse-key builders — origin-namespaced so identical publishers don't merge. */
const srcKey = (origin: AssetOrigin, publisher: string) => `${origin}:${publisher}`;
const domainKey = (origin: AssetOrigin, publisher: string, slug: string) =>
  `${origin}:${publisher}:dom:${slug}`;

/**
 * Collapse-key builders for the Sources tree (`org → repo → domain`). Kept
 * separate from the asset-tree keys above so the Sources tab collapses
 * independently of the Assets tab's Sources band (different default state:
 * domains here open collapsed).
 */
const repoKey = (org: string, repo: string) => `${org}/${repo}`;
const srcDomainKey = (org: string, repo: string, slug: string) => `${org}/${repo}:dom:${slug}`;

/** Slug used for the synthetic "no domain" group (assets with no `domainSlug`). */
const NO_DOMAIN = "";

/**
 * Display registry: `domainSlug` → its metadata (title, description, relation,
 * recommended pick). Populated from the feed (`AssetFeedResult.domains`); empty
 * until the store serves `catalog.listDomains`. A slug with no registry entry
 * still renders a header (titled by its slug) — the join degrades gracefully.
 */
let domainRegistry: Map<string, CatalogDomain> = new Map();

/** Short relation badge for a domain header (`[add]`/`[alt]`/`[seq]`). */
function relationBadge(relation: DomainRelation | undefined): string {
  if (relation === "additions") return pc.dim("[") + pc.green("add") + pc.dim("]");
  if (relation === "alternatives") return pc.dim("[") + pc.yellow("alt") + pc.dim("]");
  if (relation === "sequence") return pc.dim("[") + pc.blue("seq") + pc.dim("]");
  return "";
}

/**
 * Per-asset relation hint glyph: `[add]` (additions), `▷alt` (alternatives),
 * `⇣N` (sequence step N). Empty for an asset outside any relational domain.
 */
function relationGlyph(relation: DomainRelation | undefined, seq: number | undefined): string {
  if (relation === "additions") return pc.green("[add]");
  if (relation === "alternatives") return pc.yellow("▷alt");
  if (relation === "sequence") return pc.blue(`⇣${seq ?? "?"}`);
  return "";
}

/** Plain (uncoloured) form of {@link relationGlyph} — for column-width math. */
function relationGlyphText(relation: DomainRelation | undefined, seq: number | undefined): string {
  if (relation === "additions") return "[add]";
  if (relation === "alternatives") return "▷alt";
  if (relation === "sequence") return `⇣${seq ?? "?"}`;
  return "";
}

/**
 * One row in the Libraries tab — a user authoring place (`backend: "local" | "git"`).
 * Sources (github repos registered via `skaile source add`) are represented
 * separately as {@link SourceRow}.
 */
interface LibraryRow {
  name: string;
  backend: "local" | "git";
  ownership: string; // "owner" | "contributor" | "reader"
  assetCount: number;
  isDefault: boolean;
}

/**
 * One row in the Sources tab — a third-party github repo registered via
 * `skaile source add`. Sources always live under `~/.skaile/cache/sources/` and use
 * the `git` backend; we keep them distinct from libraries in the UI to match
 * the three-noun vocabulary (source / library / store).
 */
interface SourceRow {
  name: string;
  path: string;
  ownership: string;
  assetCount: number;
}

type Tab = "assets" | "sources" | "libraries" | "pending" | "sync";
type Action = "add" | "remove" | "clear" | "toggle";

// ── State ────────────────────────────────────────────────────────────────────
//
// All mutable TUI state lives on a single object so it is easy to find and
// reason about. Functions read/write state.* directly; this avoids the
// scattered `let` declarations the file accumulated over time.
//
// The AssetManager is held separately because it has different lifetime
// semantics (write-once at startup vs. per-frame mutable state) and avoids
// a `T | undefined` placeholder on the state object.

let am: AssetManager;

interface State {
  tab: Tab;
  cursor: number;
  assetRows: AssetRow[];
  sourceRows: SourceRow[];
  libraryRows: LibraryRow[];
  visibleRows: AssetRow[];
  // Sources tab tree (org → repo → domain → asset) + its visible slice.
  sourceTreeRows: AssetRow[];
  visibleSourceRows: AssetRow[];
  pendingAdds: Set<string>;
  pendingRemoves: Set<string>;
  collapsedOrigins: Set<AssetOrigin>; // keys: origin
  collapsedSources: Set<string>; // keys: "origin:publisher"
  collapsedDomains: Set<string>; // keys: "origin:publisher:dom:<slug>"
  expandedDomainInfo: Set<string>; // keys: same as collapsedDomains — info region open
  seenDomains: Set<string>; // domain keys encountered (drives one-shot collapse-at-domain)
  // Sources-tab collapse state (independent of the Assets tab). Domains here
  // default *collapsed*; `seenSourceDomains` makes that a one-shot so a user
  // expand survives reloads.
  collapsedSourceOrgs: Set<string>; // keys: "<org>"
  collapsedSourceRepos: Set<string>; // keys: "<org>/<repo>"
  collapsedSourceDomains: Set<string>; // keys: "<org>/<repo>:dom:<slug>"
  expandedSourceDomainInfo: Set<string>; // keys: same as collapsedSourceDomains
  seenSourceDomains: Set<string>; // domain keys encountered (drives one-shot collapse-at-domain)
  seenOrigins: Set<AssetOrigin>; // origins encountered so far (drives one-shot store auto-collapse)
  originFilter: AssetOrigin | null; // when set, only this origin's rows are shown
  message: string;
  cols: number;
  awaitingExitConfirm: boolean;
}

const state: State = {
  tab: "assets",
  cursor: 0,
  assetRows: [],
  sourceRows: [],
  libraryRows: [],
  visibleRows: [],
  sourceTreeRows: [],
  visibleSourceRows: [],
  pendingAdds: new Set(),
  pendingRemoves: new Set(),
  collapsedOrigins: new Set(),
  collapsedSources: new Set(),
  collapsedDomains: new Set(),
  expandedDomainInfo: new Set(),
  seenDomains: new Set(),
  collapsedSourceOrgs: new Set(),
  collapsedSourceRepos: new Set(),
  collapsedSourceDomains: new Set(),
  expandedSourceDomainInfo: new Set(),
  seenSourceDomains: new Set(),
  seenOrigins: new Set(),
  originFilter: null,
  message: "",
  cols: 80,
  awaitingExitConfirm: false,
};

// ── Pure helpers (exported for tests) ────────────────────────────────────────

/**
 * Filter the full asset row list to only rows that should be visible given the
 * current collapsed origin / publisher / domain sets, the per-domain info-expand
 * set, and the active origin filter. The tree is flat (`origin → publisher →
 * domain → asset`, domain slugs not nested), so collapse checks are exact set
 * membership — no prefix walking. Collapse keys are origin-namespaced so the
 * same publisher under two origins collapses independently.
 *
 * - Every row is hidden when an `originFilter` is set and the row's origin
 *   doesn't match it.
 * - An `origin-header` is otherwise always visible.
 * - A `publisher-header` is hidden when its origin is collapsed.
 * - A `domain-header` is hidden when its origin or its `"origin:publisher"` key
 *   is collapsed.
 * - A `domain-info` line follows the same rule as the domain's assets (shown
 *   when the band is open). The `[d]` toggle changes only how it *renders*
 *   (truncated ↔ full), not whether it shows.
 * - An asset under a real domain is hidden when that `"…:dom:<slug>"` key is
 *   collapsed; a "no domain" asset only depends on its origin + publisher.
 *
 * @param rows - Complete flat list of asset rows.
 * @param collapsedOrigins - Set of origins whose children are hidden.
 * @param collapsedSources - Set of `"origin:publisher"` keys whose children are hidden.
 * @param collapsedDomains - Set of `"origin:publisher:dom:<slug>"` keys whose assets are hidden.
 * @param originFilter - When set, only rows of this origin are kept.
 * @returns Filtered array of visible rows.
 * @docLink cli/commands/manage#make-manage-command
 */
export function buildVisibleRows(
  rows: AssetRow[],
  collapsedOrigins: Set<AssetOrigin>,
  collapsedSources: Set<string>,
  collapsedDomains: Set<string>,
  originFilter: AssetOrigin | null = null,
): AssetRow[] {
  return rows.filter((row) => {
    const origin = rowOrigin(row);
    if (originFilter && origin !== originFilter) return false;
    if (row.type === "origin-header") return true;
    if (collapsedOrigins.has(origin)) return false;
    if (row.type === "publisher-header") return true;
    if (collapsedSources.has(srcKey(origin, rowGroup(row)))) return false;
    if (row.type === "domain-header") return true;
    if (row.type === "domain-info") {
      return !collapsedDomains.has(domainKey(origin, row.source!, row.domainSlug ?? NO_DOMAIN));
    }
    // asset: a real domain (underDomain) is gated on its domain collapse key;
    // a "no domain" asset hangs directly under its group (already cleared).
    if (row.underDomain) {
      return !collapsedDomains.has(domainKey(origin, rowGroup(row), rowDomain(row)));
    }
    return true;
  });
}

/**
 * Return `kind:name` refs for all asset rows belonging to a specific origin.
 *
 * @docLink cli/commands/manage#make-manage-command
 */
export function originAssetRefs(rows: AssetRow[], origin: AssetOrigin): string[] {
  return refsOf(rows.filter((r) => isAsset(r) && rowOrigin(r) === origin));
}

/**
 * Return `kind:name` refs for all asset rows belonging to a specific
 * origin + publisher + domain slug (`""` = the "no domain" group).
 *
 * @docLink cli/commands/manage#make-manage-command
 */
export function domainAssetRefs(
  rows: AssetRow[],
  origin: AssetOrigin,
  publisher: string,
  slug: string,
): string[] {
  return refsOf(rows.filter((r) => isAsset(r) && inDomain(r, origin, publisher, slug)));
}

/**
 * Return `kind:name` refs for all asset rows belonging to a specific
 * origin + publisher (across its repos).
 *
 * @docLink cli/commands/manage#make-manage-command
 */
export function sourceAssetRefs(rows: AssetRow[], origin: AssetOrigin, source: string): string[] {
  return refsOf(rows.filter((r) => isAsset(r) && inSource(r, origin, source)));
}

// ── Sources tree (org → repo → domain → asset) ───────────────────────────────
//
// The Sources tab renders the `source`-origin slice of the asset feed as a
// collapsible tree with one extra band — the github **org** — above the repo.
// It reuses the same flat-list/visible-filter shape as the Assets tree, with
// its own collapse keys (`<org>`, `<org>/<repo>`, `<org>/<repo>:dom:<slug>`).

/**
 * Build the flat Sources-tree row list from the source-origin asset rows.
 * Hierarchy: a single static "Sources" root → per github org → per repo → "no
 * domain" assets (directly under the repo) → per real domain (header + optional
 * info + assets). `registered` injects org/repo bands for sources that have no
 * discoverable assets yet, so a freshly-added source still shows up.
 *
 * @docLink cli/commands/manage#make-manage-command
 */
export function buildSourceTreeRows(
  assetRows: AssetRow[],
  registered: { org: string; repo: string }[] = [],
): AssetRow[] {
  // org → repo → slug → assets
  const byOrg = new Map<string, Map<string, Map<string, AssetRow[]>>>();
  const ensure = (org: string, repo: string) => {
    if (!byOrg.has(org)) byOrg.set(org, new Map());
    const byRepo = byOrg.get(org)!;
    if (!byRepo.has(repo)) byRepo.set(repo, new Map());
    return byRepo.get(repo)!;
  };

  for (const r of assetRows) {
    if (!isAsset(r) || rowOrigin(r) !== "source") continue;
    const org = rowOrg(r);
    const repo = rowGroup(r);
    const slug = rowDomain(r);
    const byDomain = ensure(org, repo);
    if (!byDomain.has(slug)) byDomain.set(slug, []);
    byDomain.get(slug)!.push(r);
  }
  // Make empty registered sources visible (org/repo band, no assets).
  for (const { org, repo } of registered) ensure(org, repo);

  if (byOrg.size === 0) return [];

  const rows: AssetRow[] = [{ type: "source-root" }];
  for (const org of [...byOrg.keys()].sort((a, b) => a.localeCompare(b))) {
    rows.push({ type: "org-header", origin: "source", org });
    const byRepo = byOrg.get(org)!;
    for (const repo of [...byRepo.keys()].sort((a, b) => a.localeCompare(b))) {
      rows.push({ type: "repo-header", origin: "source", org, source: repo });
      const byDomain = byRepo.get(repo)!;
      const loose = byDomain.get(NO_DOMAIN);
      if (loose) for (const a of loose) rows.push({ ...a, org });
      for (const slug of sortedDomainSlugs([...byDomain.keys()])) {
        const meta = domainRegistry.get(slug);
        rows.push({ type: "domain-header", origin: "source", org, source: repo, domainSlug: slug });
        if (meta?.description) {
          rows.push({ type: "domain-info", origin: "source", org, source: repo, domainSlug: slug });
        }
        for (const a of byDomain.get(slug)!) rows.push({ ...a, org });
      }
    }
  }
  return rows;
}

/**
 * Filter the flat Sources-tree rows to those visible given the collapsed
 * org / repo / domain sets. The root is always visible; an org-header hides
 * when no set applies above it; a repo-header hides under a collapsed org; a
 * domain-header hides under a collapsed org/repo; a domain-info / domained
 * asset hides under a collapsed domain (or any collapsed ancestor).
 *
 * @docLink cli/commands/manage#make-manage-command
 */
export function buildVisibleSourceRows(
  rows: AssetRow[],
  collapsedOrgs: Set<string>,
  collapsedRepos: Set<string>,
  collapsedDomains: Set<string>,
): AssetRow[] {
  return rows.filter((row) => {
    if (row.type === "source-root" || row.type === "org-header") return true;
    const org = row.org ?? UNKNOWN_ORG;
    if (collapsedOrgs.has(org)) return false;
    if (row.type === "repo-header") return true;
    if (collapsedRepos.has(repoKey(org, row.source!))) return false;
    if (row.type === "domain-header") return true;
    if (row.type === "domain-info") {
      return !collapsedDomains.has(srcDomainKey(org, row.source!, row.domainSlug ?? NO_DOMAIN));
    }
    // asset: a domained one is gated on its domain key; a "no domain" asset
    // hangs directly under the repo (already cleared by the repo check above).
    if (row.underDomain) {
      return !collapsedDomains.has(srcDomainKey(org, rowGroup(row), rowDomain(row)));
    }
    return true;
  });
}

/** Refs of all source-tree asset rows in a github org. */
export function sourceOrgRefs(rows: AssetRow[], org: string): string[] {
  return refsOf(rows.filter((r) => isAsset(r) && rowOrigin(r) === "source" && rowOrg(r) === org));
}

/** Refs of all source-tree asset rows in a specific org + repo. */
export function sourceRepoRefs(rows: AssetRow[], org: string, repo: string): string[] {
  return refsOf(
    rows.filter(
      (r) => isAsset(r) && rowOrigin(r) === "source" && rowOrg(r) === org && rowGroup(r) === repo,
    ),
  );
}

/** Refs of all source-tree asset rows in a specific org + repo + domain slug. */
export function sourceDomainRefs(
  rows: AssetRow[],
  org: string,
  repo: string,
  slug: string,
): string[] {
  return refsOf(
    rows.filter(
      (r) =>
        isAsset(r) &&
        rowOrigin(r) === "source" &&
        rowOrg(r) === org &&
        rowGroup(r) === repo &&
        rowDomain(r) === slug,
    ),
  );
}

// ── Internal row helpers ─────────────────────────────────────────────────────

function isAsset(r: AssetRow): boolean {
  return r.type === "asset";
}

function rowOrigin(r: AssetRow): AssetOrigin {
  return r.origin ?? "source";
}

function rowPublisher(r: AssetRow): string {
  return r.entry?.publisher || "other";
}

/**
 * The row's second-level group key. Prefers the explicit `source` (set on header
 * rows and, since source-vs-publisher grouping, on asset rows too) and falls back
 * to the entry's publisher — so an asset row built without a `source` (e.g. tests)
 * still groups by publisher exactly as before.
 */
function rowGroup(r: AssetRow): string {
  return r.source ?? rowPublisher(r);
}

function rowDomain(r: AssetRow): string {
  return r.entry?.domainSlug || NO_DOMAIN;
}

/**
 * The github org for a source asset, taken from the `<org>/<repo>` provenance
 * slug `scanRepo` stamps onto `entry.repo`. Empty when unknown (the entry then
 * groups under a synthetic "(unknown)" org so it never silently disappears).
 */
const UNKNOWN_ORG = "(unknown)";
function rowOrg(r: AssetRow): string {
  if (r.org) return r.org;
  const repo = r.entry?.repo;
  if (repo?.includes("/")) return repo.slice(0, repo.indexOf("/"));
  return UNKNOWN_ORG;
}

function refsOf(rows: AssetRow[]): string[] {
  return rows.map((r) => assetRefOf(r)).filter((ref): ref is string => ref !== null);
}

function inSource(r: AssetRow, origin: AssetOrigin, source: string): boolean {
  return rowOrigin(r) === origin && rowGroup(r) === source;
}

/**
 * Returns true when the asset row belongs to `origin` + `publisher` and its
 * domain slug is exactly `slug` (`""` matches "no domain" assets that hang
 * directly under the publisher). Slugs are flat — exact match, no descendant
 * walking (domains are never nested).
 */
function inDomain(r: AssetRow, origin: AssetOrigin, publisher: string, slug: string): boolean {
  if (!inSource(r, origin, publisher)) return false;
  return rowDomain(r) === slug;
}

// Refs flow into AssetManager.add, which parses them through parseAssetRef —
// publisher is required under the canonical-identity schema. An entry with no
// publisher has no canonical identity and can't be installed, so it's not
// selectable (returns null) rather than producing a ref that throws on apply.
function assetRefOf(r: AssetRow): string | null {
  if (!isAsset(r) || !r.entry?.publisher) return null;
  return assetRefToStr({
    kind: r.entry.kind,
    name: r.entry.name,
    publisher: r.entry.publisher,
  });
}

function findAssetRow(ref: string): AssetRow | undefined {
  return state.assetRows.find((r) => isAsset(r) && assetRefOf(r) === ref);
}

// ── Selection state machine ──────────────────────────────────────────────────
//
// Every (asset, action) interaction reduces to setSelection. Bulk operations
// from headers map over the children with bulkSelection.
//
// Action semantics:
//   "add"     — pressing + intent: cancel any pending remove, and queue an add
//               if the asset is not yet installed
//   "remove"  — pressing - intent: cancel any pending add, and queue a remove
//               if the asset is currently installed
//   "clear"   — clear any pending state for this ref
//   "toggle"  — context-aware: if any pending → clear, else add or remove
//               based on whether the asset is currently installed
//
// "add" and "remove" always cancel the opposite-pending state first, so the
// user can press +/- to undo a queued opposite action. The early return on
// already-installed (for add) or not-yet-installed (for remove) is checked AFTER
// that cleanup, which keeps installed assets from ever ending up in pendingAdds
// (and vice-versa).

function setSelection(ref: string, action: Action): void {
  const row = findAssetRow(ref);
  if (!row) return;

  if (action === "clear") {
    state.pendingAdds.delete(ref);
    state.pendingRemoves.delete(ref);
    return;
  }

  let resolved: "add" | "remove";
  if (action === "toggle") {
    if (state.pendingAdds.has(ref) || state.pendingRemoves.has(ref)) {
      state.pendingAdds.delete(ref);
      state.pendingRemoves.delete(ref);
      return;
    }
    resolved = row.installed ? "remove" : "add";
  } else {
    resolved = action;
  }

  if (resolved === "add") {
    state.pendingRemoves.delete(ref); // pressing + always cancels a pending remove
    if (row.installed) return; // already installed → nothing to queue
    state.pendingAdds.add(ref);
  } else {
    state.pendingAdds.delete(ref); // pressing - always cancels a pending add
    if (!row.installed) return; // not installed → nothing to queue
    state.pendingRemoves.add(ref);
  }
}

function bulkSelection(refs: string[], action: Action): void {
  for (const ref of refs) setSelection(ref, action);
}

/**
 * Apply a header-targeted action; returns true if `row` is a header type (so the
 * caller suppresses the per-asset fallback). Origin and publisher headers
 * bulk-install their subtree. A **domain header is navigational** — it is
 * "handled" (returns true → +/- do nothing) but never bulk-installs, because a
 * domain is a discovery grouping, not a bundle. `domain-info` rows likewise.
 */
function actOnHeader(row: AssetRow, action: Action): boolean {
  if (row.type === "origin-header") {
    bulkSelection(originAssetRefs(state.assetRows, rowOrigin(row)), action);
    return true;
  }
  if (row.type === "publisher-header") {
    bulkSelection(sourceAssetRefs(state.assetRows, rowOrigin(row), row.source!), action);
    return true;
  }
  return row.type === "domain-header" || row.type === "domain-info";
}

// ── Data loading ─────────────────────────────────────────────────────────────

/**
 * Per-ref cache of the footer soft-dep preview line (keyed by `<mode>:kind:@pub/name`).
 * Computing it opens the index, so we memoize and only recompute on
 * highlight/selection change — never per render frame. The mode prefix keeps the
 * add-preview and remove-preview lines distinct for the same ref. Cleared on
 * `loadAssets` because the absent/orphan result changes once an asset is added/removed.
 */
const softDepPreviewCache = new Map<string, string>();

/**
 * Preview the soft deps of the asset currently under the cursor (Assets/Sources
 * tabs) in the footer. A present/removable asset previews what removing it would
 * orphan (`removing <name> would orphan <x> (soft dep)`); an absent asset previews
 * the soft deps adding it would reference (`<name> references <x> (soft dep, not
 * auto-added)`). Report-only — highlighting/selecting never auto-adds or -removes.
 * No-op on non-asset rows.
 */
/**
 * The `mode:ref` preview cache key for the row currently under the cursor, or
 * `null` on a non-previewable row. Used to detect a cursor move across an await.
 */
function softDepCacheKeyUnderCursor(): string | null {
  if (state.tab !== "assets" && state.tab !== "sources") return null;
  const rows = state.tab === "sources" ? state.visibleSourceRows : state.visibleRows;
  const row = rows[state.cursor];
  const ref = row?.type === "asset" ? assetRefOf(row) : null;
  if (!row || !ref) return null;
  return `${row.installed ? "remove" : "add"}:${ref}`;
}

async function previewSoftDepsUnderCursor(): Promise<void> {
  if (state.tab !== "assets" && state.tab !== "sources") return;
  const rows = state.tab === "sources" ? state.visibleSourceRows : state.visibleRows;
  const row = rows[state.cursor];
  const ref = row?.type === "asset" ? assetRefOf(row) : null;
  if (!row || !ref) {
    state.message = ""; // clear any stale preview when not on a previewable asset
    return;
  }
  // Present assets are removable → preview their orphans; absent assets are
  // addable → preview the soft deps they'd reference.
  const mode = row.installed ? "remove" : "add";
  const cacheKey = `${mode}:${ref}`;
  let msg = softDepPreviewCache.get(cacheKey);
  if (msg === undefined) {
    if (mode === "remove") {
      const targets = (await am.orphanedSoftDepsAfterRemoving([ref])).map((o) =>
        bareSoftDepName(o.target),
      );
      msg = formatSoftDepRemovePreview(row.entry!.name, targets);
    } else {
      const targets = (await am.reportSoftDeps([ref])).map((e) => bareSoftDepName(e.to));
      msg = formatSoftDepAddPreview(row.entry!.name, targets);
    }
    softDepPreviewCache.set(cacheKey, msg);
  }
  // The index read may have outlived the cursor (rapid j/k): only write if the
  // cursor still sits on the row we computed for, else a slower earlier call
  // would clobber a newer row's preview.
  if (softDepCacheKeyUnderCursor() === cacheKey) state.message = msg;
}

async function loadAssets(): Promise<void> {
  softDepPreviewCache.clear();
  const { gatherAssetFeeds } = await import("../asset-feeds.ts");
  const { entries, domains, notes } = await gatherAssetFeeds(am, am.projectDir);
  if (notes.length > 0) {
    state.message = notes.map((n) => `[${n.feed}] ${n.message}`).join(" • ");
  }
  domainRegistry = new Map(domains.map((d) => [d.slug, d]));
  const installed = new Set(am.listDeployed().map((e) => `${e.kind}:${e.name}`));

  const byOrigin = groupEntriesByOriginPublisherDomain(entries);
  const rows = buildAssetRowsFromGroups(byOrigin, installed);
  state.assetRows = rows;

  reconcileCollapsedSets(rows);
  rebuildVisible();
  rebuildSourceTree();
}

/**
 * Rebuild the Sources-tab tree from the current asset rows (the source-origin
 * slice) plus the registered-source skeleton, reconcile its collapse sets
 * (orgs/repos default open; domains default collapsed, one-shot), and refresh
 * the visible slice. Called whenever the asset rows or the source list change.
 */
function rebuildSourceTree(): void {
  const registered = state.sourceRows
    .map((s) => sourceOrgRepo(s))
    .filter((x): x is NonNullable<typeof x> => x !== null);
  state.sourceTreeRows = buildSourceTreeRows(state.assetRows, registered);
  reconcileSourceCollapsedSets(state.sourceTreeRows);
  rebuildVisibleSources();
}

/**
 * Derive `{org, repo}` from a registered source's cache path
 * (`…/sources/<host>/<owner>/<repo>`). Returns null when the path is too short
 * to carry an owner segment.
 */
function sourceOrgRepo(s: SourceRow): { org: string; repo: string } | null {
  const parts = s.path.split(path.sep).filter(Boolean);
  if (parts.length < 2) return null;
  return { org: parts[parts.length - 2]!, repo: parts[parts.length - 1]! };
}

/**
 * Reconcile the Sources-tab collapse sets with new tree rows. Orgs and repos
 * default open; domains default *collapsed* — but only the first time each is
 * seen (`seenSourceDomains`), so a user expand is not clobbered on reload.
 * Stale keys (for rows that vanished) are pruned from every set.
 */
function reconcileSourceCollapsedSets(rows: AssetRow[]): void {
  const orgKeys = new Set(rows.filter((r) => r.type === "org-header").map((r) => r.org!));
  const repoKeys = new Set(
    rows.filter((r) => r.type === "repo-header").map((r) => repoKey(r.org!, r.source!)),
  );
  const domainKeys = new Set(
    rows
      .filter((r) => r.type === "domain-header")
      .map((r) => srcDomainKey(r.org!, r.source!, r.domainSlug ?? NO_DOMAIN)),
  );

  for (const k of state.collapsedSourceOrgs)
    if (!orgKeys.has(k)) state.collapsedSourceOrgs.delete(k);
  for (const k of state.collapsedSourceRepos) {
    if (!repoKeys.has(k)) state.collapsedSourceRepos.delete(k);
  }
  // One-shot collapse-at-domain: collapse each domain the first time we see it.
  for (const k of domainKeys) {
    if (!state.seenSourceDomains.has(k)) {
      state.collapsedSourceDomains.add(k);
      state.seenSourceDomains.add(k);
    }
  }
  for (const k of state.collapsedSourceDomains) {
    if (!domainKeys.has(k)) state.collapsedSourceDomains.delete(k);
  }
  for (const k of state.expandedSourceDomainInfo) {
    if (!domainKeys.has(k)) state.expandedSourceDomainInfo.delete(k);
  }
  for (const k of state.seenSourceDomains)
    if (!domainKeys.has(k)) state.seenSourceDomains.delete(k);
}

function rebuildVisibleSources(): void {
  state.visibleSourceRows = buildVisibleSourceRows(
    state.sourceTreeRows,
    state.collapsedSourceOrgs,
    state.collapsedSourceRepos,
    state.collapsedSourceDomains,
  );
  clampCursor();
}

/**
 * Group feed entries by origin (the feed), then by their second-level group
 * (`OriginEntry.group` — the source name under Sources, else `entry.publisher`),
 * then by `domainSlug` (`""` = no domain → assets hang directly under their
 * group). The same group can appear under multiple origins, so origin is the
 * outermost map key.
 */
function groupEntriesByOriginPublisherDomain(
  entries: OriginEntry[],
): Map<AssetOrigin, Map<string, Map<string, CatalogEntry[]>>> {
  const byOrigin = new Map<AssetOrigin, Map<string, Map<string, CatalogEntry[]>>>();
  for (const { origin, entry, group } of entries) {
    const publisher = group || entry.publisher || "other";
    const slug = entry.domainSlug || NO_DOMAIN;
    if (!byOrigin.has(origin)) byOrigin.set(origin, new Map());
    const byPublisher = byOrigin.get(origin)!;
    if (!byPublisher.has(publisher)) byPublisher.set(publisher, new Map());
    const byDomain = byPublisher.get(publisher)!;
    if (!byDomain.has(slug)) byDomain.set(slug, []);
    byDomain.get(slug)!.push(entry);
  }
  return byOrigin;
}

/** Sort a publisher's real domain slugs by registry `order`, then slug. */
function sortedDomainSlugs(slugs: string[]): string[] {
  return slugs
    .filter((s) => s !== NO_DOMAIN)
    .sort((a, b) => {
      const oa = domainRegistry.get(a)?.order ?? Number.MAX_SAFE_INTEGER;
      const ob = domainRegistry.get(b)?.order ?? Number.MAX_SAFE_INTEGER;
      return oa !== ob ? oa - ob : a.localeCompare(b);
    });
}

/**
 * Emit origin-header → publisher-header → "no domain" assets (directly under the
 * publisher) → per real domain: domain-header → optional domain-info → assets.
 * Domains are never nested, so a flat per-slug emit is correct. "No domain"
 * assets stay headerless so a domain-free catalog (the common case until the
 * store serves domains) renders exactly like a flat publisher list.
 */
function buildAssetRowsFromGroups(
  byOrigin: Map<AssetOrigin, Map<string, Map<string, CatalogEntry[]>>>,
  installed: Set<string>,
): AssetRow[] {
  const rows: AssetRow[] = [];
  const sortItems = (items: CatalogEntry[]) =>
    items.sort((a, b) =>
      a.kind !== b.kind ? a.kind.localeCompare(b.kind) : a.name.localeCompare(b.name),
    );
  const pushAsset = (
    origin: AssetOrigin,
    group: string,
    entry: CatalogEntry,
    underDomain: boolean,
    seq?: number,
  ) => {
    const ref = `${entry.kind}:${entry.name}`;
    // `source` carries the group on assets too, so collapse/selection keys match
    // the headers even when the group differs from the publisher (Sources band).
    // `org` (github org from `entry.repo`) is the Sources-tree grouping axis;
    // harmless on the Assets tab, which never reads it.
    rows.push({
      type: "asset",
      origin,
      source: group,
      org: entry.repo?.includes("/") ? entry.repo.slice(0, entry.repo.indexOf("/")) : undefined,
      entry,
      installed: installed.has(ref),
      underDomain,
      seq,
    });
  };

  for (const origin of ORIGIN_ORDER) {
    const byGroup = byOrigin.get(origin);
    if (!byGroup || byGroup.size === 0) continue;
    rows.push({ type: "origin-header", origin });
    for (const [group, byDomain] of [...byGroup.entries()].sort((a, b) =>
      a[0].localeCompare(b[0]),
    )) {
      rows.push({ type: "publisher-header", origin, source: group });

      // "No domain" assets render directly under the group (no header).
      const loose = byDomain.get(NO_DOMAIN);
      if (loose) for (const entry of sortItems(loose)) pushAsset(origin, group, entry, false);

      // Real domains, ordered, each as a collapsible labelled group.
      for (const slug of sortedDomainSlugs([...byDomain.keys()])) {
        const meta = domainRegistry.get(slug);
        rows.push({ type: "domain-header", origin, source: group, domainSlug: slug });
        if (meta?.description) {
          rows.push({ type: "domain-info", origin, source: group, domainSlug: slug });
        }
        const items = sortItems(byDomain.get(slug)!);
        const sequenced = meta?.relation === "sequence";
        items.forEach((entry, i) =>
          pushAsset(origin, group, entry, true, sequenced ? i + 1 : undefined),
        );
      }
    }
  }
  return rows;
}

/** Reconcile the collapsed origin / source / domain sets with the new rows. */
function reconcileCollapsedSets(rows: AssetRow[]): void {
  // Origins default open, except `store`, which is auto-collapsed the first time
  // it appears (a one-shot — a later expand survives reloads after apply).
  const presentOrigins = new Set(rows.filter((r) => r.type === "origin-header").map(rowOrigin));
  for (const o of presentOrigins) {
    if (!state.seenOrigins.has(o)) {
      if (o === "store") state.collapsedOrigins.add(o);
      state.seenOrigins.add(o);
    }
  }
  for (const o of state.collapsedOrigins) {
    if (!presentOrigins.has(o)) state.collapsedOrigins.delete(o);
  }
  if (state.originFilter && !presentOrigins.has(state.originFilter)) state.originFilter = null;

  // Publisher headers default open. Prune stale keys.
  const newSources = new Set(
    rows.filter((r) => r.type === "publisher-header").map((r) => srcKey(rowOrigin(r), r.source!)),
  );
  for (const s of state.collapsedSources) {
    if (!newSources.has(s)) state.collapsedSources.delete(s);
  }

  const newDomainKeys = new Set(
    rows
      .filter((r) => r.type === "domain-header")
      .map((r) => domainKey(rowOrigin(r), r.source!, r.domainSlug ?? NO_DOMAIN)),
  );
  for (const k of newDomainKeys) {
    if (!state.seenDomains.has(k)) {
      state.collapsedDomains.add(k);
      state.seenDomains.add(k);
    }
  }
  for (const d of state.collapsedDomains) {
    if (!newDomainKeys.has(d)) state.collapsedDomains.delete(d);
  }
  for (const d of state.expandedDomainInfo) {
    if (!newDomainKeys.has(d)) state.expandedDomainInfo.delete(d);
  }
  // Prune stale keys — a removed-then-re-added domain is treated as new and re-collapsed.
  for (const d of state.seenDomains) {
    if (!newDomainKeys.has(d)) state.seenDomains.delete(d);
  }
}

/**
 * Minimal scaffold for the Sources and Libraries tabs: fetch registered
 * libraries via LibraryManager, then split them into sources (github clones
 * under `~/.skaile/cache/sources/`) and authoring libraries. Rich rendering is
 * deferred to AF-LIB-TUI-RICH.
 */
async function loadSourcesAndLibraries(): Promise<void> {
  try {
    const [{ openLibraryManager }, { skaileHomeDir }, { getGlobalCacheDir }] = await Promise.all([
      import("../open-library.ts"),
      import("@skaile/workspaces/library"),
      import("@skaile/workspaces/core"),
    ]);
    const { manager, library, close } = await openLibraryManager();
    try {
      // index.db rows predating the cache migration still carry the legacy
      // ~/.skaile/sources path, so tolerate both roots.
      const newRoot = getGlobalCacheDir();
      const legacyRoot = path.join(skaileHomeDir(), "sources");
      const isSourceRow = (l: { path: string }) =>
        l.path.startsWith(newRoot) || l.path.startsWith(legacyRoot);

      const libs = await manager.listLibraries();
      const defs = await library.listAssetDefs();
      const counts = new Map<string, number>();
      for (const d of defs) {
        const id = d.libraryId ?? "";
        counts.set(id, (counts.get(id) ?? 0) + 1);
      }

      state.sourceRows = libs.filter(isSourceRow).map((l) => ({
        name: l.name,
        path: l.path,
        ownership: l.ownership,
        assetCount: counts.get(l.id) ?? 0,
      }));

      state.libraryRows = libs
        .filter((l) => !isSourceRow(l))
        .map((l) => ({
          name: l.name,
          backend: l.backend as "local" | "git",
          ownership: l.ownership,
          assetCount: counts.get(l.id) ?? 0,
          isDefault: l.isDefault,
        }));
    } finally {
      close();
    }
  } catch {
    // No DB yet / open failed — leave the lists empty; tabs show emptyMsg.
    state.sourceRows = [];
    state.libraryRows = [];
  }
  // Fold the registered-source skeleton into the tree so empty sources show.
  rebuildSourceTree();
}

function rebuildVisible(): void {
  state.visibleRows = buildVisibleRows(
    state.assetRows,
    state.collapsedOrigins,
    state.collapsedSources,
    state.collapsedDomains,
    state.originFilter,
  );
  clampCursor();
}

// ── Cursor / tab ─────────────────────────────────────────────────────────────

function clampCursor(): void {
  state.cursor = Math.min(state.cursor, Math.max(0, maxCursor()));
}

function maxCursor(): number {
  return TABS[state.tab].rowCount() - 1;
}

function moveCursor(delta: number): void {
  state.cursor = Math.max(0, Math.min(maxCursor(), state.cursor + delta));
}

function nextTab(): void {
  const order: Tab[] = ["assets", "sources", "libraries", "pending", "sync"];
  state.tab = order[(order.indexOf(state.tab) + 1) % order.length];
  state.cursor = 0;
}

// ── Tab specs ────────────────────────────────────────────────────────────────
//
// Per-tab metadata used by render(), renderTabs(), maxCursor() and the footer
// hints. Adding a new tab is a single entry here plus a row type.

interface TabSpec {
  label: string;
  rowCount: () => number;
  selectableCount: () => number; // shown next to the tab label
  emptyMsg?: string;
  renderRow: (i: number, selected: boolean) => string;
  footer: string;
}

const TABS: Record<Tab, TabSpec> = {
  assets: {
    label: "Assets",
    rowCount: () => state.visibleRows.length,
    selectableCount: () => state.assetRows.filter(isAsset).length,
    renderRow: (i, sel) => renderAssetRow(state.visibleRows[i], sel),
    footer:
      "  ↑↓/jk nav  ←/→ collapse/expand  [space/+/-] select  [a/↵] apply  [d] domain info  [i] info  [1]lib [2]src [3]factory [4]store [0]all  Tab  [q] quit",
  },
  sources: {
    label: "Sources",
    rowCount: () => state.visibleSourceRows.length,
    selectableCount: () => state.sourceTreeRows.filter(isAsset).length,
    emptyMsg:
      "  No sources registered. Run `skaile source add <git-url>` from the shell to track a github repo.",
    renderRow: (i, sel) => renderSourceTreeRow(state.visibleSourceRows[i], sel),
    footer:
      "  ↑↓/jk nav  ←/→ collapse/expand  [space/+/-] select  [a/↵] apply  [d] domain info  [i] info  Tab  [q] quit",
  },
  libraries: {
    label: "Libraries",
    rowCount: () => state.libraryRows.length,
    selectableCount: () => state.libraryRows.length,
    emptyMsg:
      "  No libraries registered. Run `skaile library init <name>` from the shell to author your own.",
    renderRow: (i, sel) => renderLibraryRow(state.libraryRows[i], sel),
    footer: "  ↑↓ navigate  Tab switch  q quit  (use `skaile library …` from the shell for CRUD)",
  },
  pending: {
    label: "Pending",
    rowCount: () => 0,
    selectableCount: () => state.pendingAdds.size + state.pendingRemoves.size,
    emptyMsg: "  (no pending changes — implement in follow-up: AF-LIB-TUI-RICH)",
    renderRow: () => "",
    footer: "  Tab switch  q quit",
  },
  sync: {
    label: "Sync",
    rowCount: () => 0,
    selectableCount: () => 0,
    emptyMsg:
      "  Press S to sync all sources + libraries (rich rendering deferred to AF-LIB-TUI-RICH).",
    renderRow: () => "",
    footer: "  s sync all  Tab switch  q quit",
  },
};

// ── Terminal helpers ─────────────────────────────────────────────────────────

function clearScreen(): void {
  process.stdout.write("\x1b[2J\x1b[H");
}
function hideCursor(): void {
  process.stdout.write("\x1b[?25l");
}
function showCursor(): void {
  process.stdout.write("\x1b[?25h");
}

// ── Rendering ────────────────────────────────────────────────────────────────

function renderTabs(): string {
  return (Object.entries(TABS) as [Tab, TabSpec][])
    .map(([id, spec]) => {
      const text = `${spec.label} (${spec.selectableCount()})`;
      return id === state.tab ? pc.bgCyan(pc.black(` ${text} `)) : pc.dim(` ${text} `);
    })
    .join(pc.dim("│"));
}

/** Pending +/- counts for the subset of asset rows matched by `match`. */
function pendingCountsFor(match: (r: AssetRow) => boolean): [number, number] {
  let add = 0;
  let remove = 0;
  for (const ref of state.pendingAdds) {
    const r = findAssetRow(ref);
    if (r && match(r)) add++;
  }
  for (const ref of state.pendingRemoves) {
    const r = findAssetRow(ref);
    if (r && match(r)) remove++;
  }
  return [add, remove];
}

/** Selection indicator for a header group — mirrors the per-asset status. */
function groupStatus(refs: string[]): string {
  if (refs.length === 0) return pc.dim("· ");
  if (refs.some((r) => state.pendingAdds.has(r))) return pc.blue("+ ");
  if (refs.some((r) => state.pendingRemoves.has(r))) return pc.red("- ");
  const allInstalled = refs.every((r) => findAssetRow(r)?.installed);
  return allInstalled ? pc.green("✓ ") : pc.dim("· ");
}

function renderHeaderLine(opts: {
  indent: string;
  collapsed: boolean;
  match: (r: AssetRow) => boolean;
  refs: string[];
  label: string;
  selected: boolean;
  rows?: AssetRow[]; // row set to count over (defaults to the Assets-tab rows)
}): string {
  const indicator = opts.collapsed ? "▶" : "▼";
  const countRows = opts.rows ?? state.assetRows;
  const total = countRows.filter(opts.match).length;
  const installed = countRows.filter((r) => opts.match(r) && r.installed).length;
  const [add, remove] = pendingCountsFor(opts.match);
  let count = String(installed);
  if (add > 0) count += pc.green(`+${add}`);
  if (remove > 0) count += pc.red(`-${remove}`);
  count += `/${total}`;
  const status = groupStatus(opts.refs);
  const line = `${opts.indent}${status}${indicator} ${opts.label}  (${count} assets)`;
  return opts.selected ? pc.bgWhite(pc.black(`${line} `.padEnd(state.cols))) : line;
}

function renderAssetRow(row: AssetRow, selected: boolean): string {
  if (row.type === "origin-header") {
    const origin = rowOrigin(row);
    const meta = ORIGIN_META[origin];
    return renderHeaderLine({
      indent: " ",
      collapsed: state.collapsedOrigins.has(origin),
      match: (r) => isAsset(r) && rowOrigin(r) === origin,
      refs: originAssetRefs(state.assetRows, origin),
      label: `${meta.icon} ${pc.bold(pc.cyan(meta.label))}`,
      selected,
    });
  }

  if (row.type === "publisher-header") {
    const origin = rowOrigin(row);
    return renderHeaderLine({
      indent: " ".repeat(3),
      collapsed: state.collapsedSources.has(srcKey(origin, row.source!)),
      match: (r) => isAsset(r) && inSource(r, origin, row.source!),
      refs: sourceAssetRefs(state.assetRows, origin, row.source!),
      label: pc.bold(pc.cyan(row.source!)),
      selected,
    });
  }

  if (row.type === "domain-header") {
    const origin = rowOrigin(row);
    const slug = row.domainSlug ?? NO_DOMAIN;
    const meta = domainRegistry.get(slug);
    const title = meta?.title || slug || "(no domain)";
    const badge = relationBadge(meta?.relation);
    // ◈ marks a domain (vs the publisher band above); navigational — no install.
    const label = `${pc.magenta("◈")} ${pc.bold(title)}${badge ? ` ${badge}` : ""}`;
    return renderHeaderLine({
      indent: " ".repeat(5),
      collapsed: state.collapsedDomains.has(domainKey(origin, row.source!, slug)),
      match: (r) => isAsset(r) && inDomain(r, origin, row.source!, slug),
      refs: domainAssetRefs(state.assetRows, origin, row.source!, slug),
      label,
      selected,
    });
  }

  if (row.type === "domain-info") {
    const origin = rowOrigin(row);
    const slug = row.domainSlug ?? NO_DOMAIN;
    const key = domainKey(origin, row.source!, slug);
    const line = renderDomainInfoLine(
      domainRegistry.get(slug),
      origin,
      row.source!,
      slug,
      state.expandedDomainInfo.has(key),
    );
    return selected ? pc.inverse(line.padEnd(state.cols)) : line;
  }

  return renderAssetLeaf(row, selected);
}

/**
 * Render a single asset row (the tree leaf). Shared by the Assets and Sources
 * tabs. `looseIndent` is the column of a "no domain" asset; a domained asset
 * sits two columns deeper. The Sources tree passes a larger base because its
 * extra org band pushes everything one level right.
 */
function renderAssetLeaf(row: AssetRow, selected: boolean, looseIndent = 5): string {
  const e = row.entry!;
  const ref = assetRefOf(row) ?? "";
  let status: string;
  if (state.pendingAdds.has(ref)) status = pc.blue("+ ");
  else if (state.pendingRemoves.has(ref)) status = pc.red("- ");
  else if (row.installed) status = pc.green("✓ ");
  else status = pc.dim("· ");

  // Relation hint + recommended star come from the asset's domain (display-only).
  const dm = row.underDomain ? domainRegistry.get(rowDomain(row)) : undefined;
  const recommended = !!dm?.recommendedRef && dm.recommendedRef === ref;
  const hintStr = [relationGlyph(dm?.relation, row.seq), recommended ? pc.yellow("★") : ""]
    .filter(Boolean)
    .join(" ");
  const hintWidthText = [relationGlyphText(dm?.relation, row.seq), recommended ? "★" : ""]
    .filter(Boolean)
    .join(" ");
  const hint = hintStr ? `${hintStr} ` : "";
  const hintWidth = hintWidthText ? hintWidthText.length + 1 : 0;

  const kind = kindColorPad(e.kind);
  // fitWidth (not padEnd) so an over-long name truncates instead of overflowing
  // the row — the desc budget below assumes name is exactly 30 columns wide.
  const name = fitWidth(e.name, 30);
  // Category renders as a trailing badge (a metadata facet, not a tree axis).
  // `tier` is not threaded onto CatalogEntry, so v1 never fabricates one.
  const badge = e.category ? `${pc.dim("[")}${pc.cyan(e.category)}${pc.dim("]")} ` : "";
  // Badge sits between name and desc, so its visible width eats into the desc
  // budget — without this the `[category] ` prefix overflows the row width.
  const badgeWidth = e.category ? e.category.length + 3 : 0;
  // repo is now a dim provenance badge, not a grouping axis — bare name only.
  const repoBare = e.repo
    ? e.repo.includes("/")
      ? e.repo.slice(e.repo.indexOf("/") + 1)
      : e.repo
    : "";
  const repoBadge = repoBare ? pc.dim(` ·${repoBare}`) : "";
  const repoWidth = repoBare ? repoBare.length + 2 : 0;
  const desc =
    e.description?.slice(0, Math.max(0, state.cols - 56 - badgeWidth - hintWidth - repoWidth)) ??
    "";
  // Indent: a domained asset sits one step deeper than its "no domain" sibling.
  const indent = " ".repeat(looseIndent + (row.underDomain ? 2 : 0));
  const line = `${indent}${status} ${hint}${kind} ${name} ${badge}${pc.dim(desc)}${repoBadge}`;
  return selected ? pc.inverse(line.padEnd(state.cols)) : line;
}

/**
 * Render a row of the Sources tab tree (`Sources → org → repo → domain →
 * asset`). Headers count + bulk-select over the Sources-tree rows and read the
 * Sources-tab collapse sets; asset leaves reuse {@link renderAssetLeaf} with a
 * deeper base indent (the org band shifts everything one level right).
 */
function renderSourceTreeRow(row: AssetRow, selected: boolean): string {
  if (row.type === "source-root") {
    const total = state.sourceTreeRows.filter(isAsset).length;
    const meta = ORIGIN_META.source;
    const line = ` ▼ ${meta.icon} ${pc.bold(pc.cyan(meta.label))}  (${total} assets)`;
    return selected ? pc.bgWhite(pc.black(`${line} `.padEnd(state.cols))) : line;
  }

  if (row.type === "org-header") {
    const org = row.org!;
    return renderHeaderLine({
      indent: " ".repeat(3),
      collapsed: state.collapsedSourceOrgs.has(org),
      match: (r) => isAsset(r) && rowOrigin(r) === "source" && rowOrg(r) === org,
      refs: sourceOrgRefs(state.sourceTreeRows, org),
      label: pc.bold(pc.cyan(org)),
      selected,
      rows: state.sourceTreeRows,
    });
  }

  if (row.type === "repo-header") {
    const org = row.org!;
    const repo = row.source!;
    return renderHeaderLine({
      indent: " ".repeat(5),
      collapsed: state.collapsedSourceRepos.has(repoKey(org, repo)),
      match: (r) =>
        isAsset(r) && rowOrigin(r) === "source" && rowOrg(r) === org && rowGroup(r) === repo,
      refs: sourceRepoRefs(state.sourceTreeRows, org, repo),
      label: pc.bold(repo),
      selected,
      rows: state.sourceTreeRows,
    });
  }

  if (row.type === "domain-header") {
    const org = row.org!;
    const repo = row.source!;
    const slug = row.domainSlug ?? NO_DOMAIN;
    const meta = domainRegistry.get(slug);
    const title = meta?.title || slug || "(no domain)";
    const badge = relationBadge(meta?.relation);
    const label = `${pc.magenta("◈")} ${pc.bold(title)}${badge ? ` ${badge}` : ""}`;
    return renderHeaderLine({
      indent: " ".repeat(7),
      collapsed: state.collapsedSourceDomains.has(srcDomainKey(org, repo, slug)),
      match: (r) =>
        isAsset(r) &&
        rowOrigin(r) === "source" &&
        rowOrg(r) === org &&
        rowGroup(r) === repo &&
        rowDomain(r) === slug,
      refs: sourceDomainRefs(state.sourceTreeRows, org, repo, slug),
      label,
      selected,
      rows: state.sourceTreeRows,
    });
  }

  if (row.type === "domain-info") {
    const slug = row.domainSlug ?? NO_DOMAIN;
    const meta = domainRegistry.get(slug);
    const members = sourceDomainRefs(state.sourceTreeRows, row.org!, row.source!, slug).length;
    const key = srcDomainKey(row.org!, row.source!, slug);
    const line = renderSourceDomainInfoLine(meta, members, state.expandedSourceDomainInfo.has(key));
    return selected ? pc.inverse(line.padEnd(state.cols)) : line;
  }

  return renderAssetLeaf(row, selected, 7);
}

/** Sources-tab domain info line — mirrors {@link renderDomainInfoLine} at a deeper indent. */
function renderSourceDomainInfoLine(
  meta: CatalogDomain | undefined,
  members: number,
  expanded: boolean,
): string {
  const indent = " ".repeat(9);
  const desc = meta?.description ?? "";
  if (!expanded) {
    const budget = Math.max(0, state.cols - indent.length - 12);
    const shown = desc.length > budget ? `${desc.slice(0, Math.max(0, budget - 1))}…` : desc;
    return pc.dim(`${indent}ⓘ ${shown} [d]`);
  }
  const parts = [desc, `${members} members`];
  if (meta?.recommendedRef) parts.push(`★ ${meta.recommendedRef}`);
  if (meta?.sidecarSource) parts.push(`sidecar ${meta.sidecarSource}`);
  const full = parts.join(" · ");
  const budget = Math.max(0, state.cols - indent.length - 3);
  const shown = full.length > budget ? `${full.slice(0, Math.max(0, budget - 1))}…` : full;
  return pc.dim(`${indent}ⓘ ${shown}`);
}

/**
 * One-line "info region" under a domain header. Collapsed (default): a dim,
 * truncated description with a `[d]` more-affordance. Expanded (`[d]`): the full
 * description plus member count, recommended pick, and sidecar provenance —
 * truncated to the terminal width so it always occupies exactly one row.
 */
function renderDomainInfoLine(
  meta: CatalogDomain | undefined,
  origin: AssetOrigin,
  publisher: string,
  slug: string,
  expanded: boolean,
): string {
  const indent = " ".repeat(7);
  const desc = meta?.description ?? "";
  if (!expanded) {
    const budget = Math.max(0, state.cols - indent.length - 12);
    const shown = desc.length > budget ? `${desc.slice(0, Math.max(0, budget - 1))}…` : desc;
    return pc.dim(`${indent}ⓘ ${shown} [d]`);
  }
  const members = domainAssetRefs(state.assetRows, origin, publisher, slug).length;
  const parts = [desc, `${members} members`];
  if (meta?.recommendedRef) parts.push(`★ ${meta.recommendedRef}`);
  if (meta?.sidecarSource) parts.push(`sidecar ${meta.sidecarSource}`);
  const full = parts.join(" · ");
  const budget = Math.max(0, state.cols - indent.length - 3);
  const shown = full.length > budget ? `${full.slice(0, Math.max(0, budget - 1))}…` : full;
  return pc.dim(`${indent}ⓘ ${shown}`);
}

function renderLibraryRow(row: LibraryRow, selected: boolean): string {
  const mark = row.isDefault ? pc.green("▸") : " ";
  const line = `${mark} ${S.cmd(fitWidth(row.name, 16))}  ${pc.dim(row.backend.padEnd(7))}  ${pc.dim(row.ownership.padEnd(12))}  ${pc.dim(`${row.assetCount} assets`)}`;
  return selected ? pc.inverse(line.padEnd(state.cols)) : line;
}

/**
 * Rows available for the scrolling body: terminal height minus the fixed
 * header/footer chrome and the optional pending-summary / message blocks.
 */
function computeMaxBodyRows(): number {
  // Reserve rows for header (3) + footer (2) + optional pending summary + optional message.
  const pendingLines =
    state.pendingAdds.size > 0 || state.pendingRemoves.size > 0
      ? 1 + (state.pendingAdds.size > 0 ? 1 : 0) + (state.pendingRemoves.size > 0 ? 1 : 0)
      : 0;
  const msgLines = state.message ? 2 : 0;
  const overhead = 5 + pendingLines + msgLines;
  return Math.max(5, (process.stdout.rows || 30) - overhead);
}

function renderHeader(): void {
  console.log();
  const filterTag =
    state.tab === "assets" && state.originFilter
      ? `  ${pc.bgYellow(pc.black(` filter: ${ORIGIN_META[state.originFilter].label} `))} ${pc.dim("[0] clear")}`
      : "";
  console.log(`  ${pc.bold("skaile manage")}  ${renderTabs()}${filterTag}`);
  console.log(`  ${S.rule(state.cols - 4)}`);
}

function renderBody(spec: TabSpec, maxRows: number): void {
  const total = spec.rowCount();
  if (total === 0 && spec.emptyMsg) {
    console.log(pc.dim(spec.emptyMsg));
  } else {
    const start = Math.max(0, state.cursor - maxRows + 3);
    const end = Math.min(total, start + maxRows);
    for (let i = start; i < end; i++) {
      console.log(spec.renderRow(i, i === state.cursor));
    }
  }
}

function renderPendingSummary(): void {
  if (state.pendingAdds.size > 0 || state.pendingRemoves.size > 0) {
    console.log();
    if (state.pendingAdds.size > 0)
      console.log(`  ${pc.blue(`+ ${state.pendingAdds.size} to add`)}`);
    if (state.pendingRemoves.size > 0)
      console.log(`  ${pc.red(`- ${state.pendingRemoves.size} to remove`)}`);
  }
}

/** Render the one-shot message line, then clear it so it shows only once. */
function renderMessage(): void {
  if (state.message) {
    console.log();
    console.log(`  ${state.message}`);
    state.message = "";
  }
}

function render(): void {
  clearScreen();
  state.cols = process.stdout.columns || 80;

  const maxRows = computeMaxBodyRows();
  const spec = TABS[state.tab];

  renderHeader();
  renderBody(spec, maxRows);
  renderPendingSummary();
  renderMessage();

  // Footer
  console.log();
  console.log(pc.dim(spec.footer));
}

function renderExitPrompt(): void {
  clearScreen();
  console.log();
  console.log(`  ${pc.yellow("!")} ${pc.bold("You have unapplied changes:")}`);
  if (state.pendingAdds.size > 0)
    console.log(`    ${pc.blue(`+ ${state.pendingAdds.size} to add`)}`);
  if (state.pendingRemoves.size > 0)
    console.log(`    ${pc.red(`- ${state.pendingRemoves.size} to remove`)}`);
  console.log();
  console.log(
    `  Apply before exiting?  ${pc.bold("[y]")} apply   ${pc.bold("[n]")} discard   ${pc.bold("[c/Esc]")} cancel`,
  );
}

// ── Actions ──────────────────────────────────────────────────────────────────

async function applyChanges(): Promise<void> {
  if (state.pendingAdds.size === 0 && state.pendingRemoves.size === 0) {
    state.message = pc.dim("Nothing to apply.");
    return;
  }

  state.message = pc.yellow("Applying changes...");
  render();

  let added = 0;
  let removed = 0;
  const appliedAdds: string[] = [];
  const appliedRemoves: string[] = [];

  for (const ref of state.pendingRemoves) {
    if (am.remove(ref)) {
      removed++;
      appliedRemoves.push(ref);
    }
  }
  for (const ref of state.pendingAdds) {
    try {
      await am.add(ref);
      added++;
      appliedAdds.push(ref);
    } catch (err) {
      state.message = pc.red(`Failed to add ${ref}: ${err}`);
    }
  }

  state.pendingAdds.clear();
  state.pendingRemoves.clear();
  await loadAssets();

  const parts: string[] = [];
  if (added > 0) parts.push(pc.green(`+${added} added`));
  if (removed > 0) parts.push(pc.red(`-${removed} removed`));
  // Report-only soft-dep summary — surfaced, never auto-pulled.
  const softDeps = appliedAdds.length > 0 ? await am.reportSoftDeps(appliedAdds) : [];
  if (softDeps.length > 0) {
    parts.push(pc.dim(`${softDeps.length} soft dep${softDeps.length === 1 ? "" : "s"} referenced`));
  }
  // Report-only: soft-dep targets the removals left unreferenced — surfaced, never
  // auto-removed. Only those with a parseable ref (removable via `skaile remove`).
  const orphans = (
    appliedRemoves.length > 0 ? await am.orphanedSoftDepsAfterRemoving(appliedRemoves) : []
  ).filter((o) => o.targetRef);
  if (orphans.length > 0) {
    const names = orphans.map((o) => bareSoftDepName(o.target)).join(", ");
    parts.push(
      pc.dim(
        `${names} no longer referenced by any installed asset — remove with \`skaile remove\``,
      ),
    );
  }
  if (parts.length) state.message = parts.join("  ");
}

async function syncAll(): Promise<void> {
  // Minimal scaffold — rich in-TUI sync is deferred to AF-LIB-TUI-RICH.
  state.message = pc.yellow(
    "Sync is not yet driven from the manage TUI. Run `skaile source sync` (sources) or `skaile library sync <name>` (git-backed libraries) from the shell.",
  );
}

function libraryShellHint(action: string): void {
  state.message = pc.yellow(
    `${action} is a shell command. Run \`skaile library …\` from the shell.`,
  );
}

async function showInfo(): Promise<void> {
  const rows = state.tab === "sources" ? state.visibleSourceRows : state.visibleRows;
  if (state.tab !== "assets" && state.tab !== "sources") return;
  const row = rows[state.cursor];
  if (row?.type !== "asset") return;
  const e = row.entry!;

  clearScreen();
  console.log();
  console.log(`  ${kindColor(e.kind)} ${pc.bold(e.name)}`);
  console.log(`  ${S.rule(50)}`);
  console.log(`  origin:      ${pc.dim(ORIGIN_META[rowOrigin(row)].label)}`);
  if (e.version) console.log(`  version:     ${e.version}`);
  if (e.description) console.log(`  description: ${e.description}`);
  if (e.publisher) console.log(`  publisher:   ${pc.dim(e.publisher)}`);
  console.log(`  manifest:    ${pc.dim(e.source)}`);
  console.log(`  installed:   ${row.installed ? pc.green("yes") : pc.red("no")}`);
  if (e.requires.length) {
    console.log(`  requires:`);
    for (const d of e.requires) console.log(`    ${kindColor(d.kind)}:${d.name}`);
  }
  if (e.dependencies.length) {
    console.log(`  dependencies:`);
    for (const d of e.dependencies) console.log(`    ${d}`);
  }
  console.log();
  console.log(pc.dim("  Press any key to return..."));

  await new Promise<void>((resolve) => {
    const onData = () => {
      process.stdin.removeListener("data", onData);
      resolve();
    };
    process.stdin.once("data", onData);
  });
}

// ── Origin filter (assets tab) ───────────────────────────────────────────────

/**
 * Set (or toggle off) the origin filter. Passing the already-active origin, or
 * an origin not currently present, clears the filter. Pressing 0 (origin=null)
 * always clears.
 */
function setOriginFilter(origin: AssetOrigin | null): void {
  if (origin === null || state.originFilter === origin) {
    state.originFilter = null;
  } else if (state.seenOrigins.has(origin)) {
    state.originFilter = origin;
    // Make the filtered origin's contents reachable — uncollapse the band.
    state.collapsedOrigins.delete(origin);
  } else {
    state.message = pc.dim(`No ${ORIGIN_META[origin].label} assets to show.`);
    return;
  }
  state.cursor = 0;
  rebuildVisible();
}

// ── Collapse / expand (assets tab) ───────────────────────────────────────────

function expandUnderCursor(): void {
  const row = state.visibleRows[state.cursor];
  if (!row) return;
  const origin = rowOrigin(row);
  if (row.type === "origin-header" && state.collapsedOrigins.has(origin)) {
    state.collapsedOrigins.delete(origin);
    rebuildVisible();
  } else if (
    row.type === "publisher-header" &&
    state.collapsedSources.has(srcKey(origin, row.source!))
  ) {
    state.collapsedSources.delete(srcKey(origin, row.source!));
    rebuildVisible();
  } else if (row.type === "domain-header") {
    const dk = domainKey(origin, row.source!, row.domainSlug ?? NO_DOMAIN);
    if (state.collapsedDomains.has(dk)) {
      state.collapsedDomains.delete(dk);
      rebuildVisible();
    }
  }
}

function collapseOrJumpUnderCursor(): void {
  const row = state.visibleRows[state.cursor];
  if (!row) return;
  const origin = rowOrigin(row);

  if (row.type === "origin-header") {
    if (!state.collapsedOrigins.has(origin)) {
      state.collapsedOrigins.add(origin);
      rebuildVisible();
    }
    return;
  }

  if (row.type === "publisher-header") {
    const sk = srcKey(origin, row.source!);
    if (!state.collapsedSources.has(sk)) {
      state.collapsedSources.add(sk);
      rebuildVisible();
    } else {
      // Already collapsed → jump to parent origin header
      const idx = findLastIndex(
        state.visibleRows,
        state.cursor,
        (r: AssetRow) => r.type === "origin-header",
      );
      if (idx >= 0) state.cursor = idx;
    }
    return;
  }

  if (row.type === "domain-header") {
    const dk = domainKey(origin, row.source!, row.domainSlug ?? NO_DOMAIN);
    if (!state.collapsedDomains.has(dk)) {
      state.collapsedDomains.add(dk);
      rebuildVisible();
    } else {
      // Already collapsed → jump to parent publisher header
      const idx = findLastIndex(
        state.visibleRows,
        state.cursor,
        (r: AssetRow) => r.type === "publisher-header",
      );
      if (idx >= 0) state.cursor = idx;
    }
    return;
  }

  // asset / domain-info → jump to nearest header above
  const idx = findLastIndex(
    state.visibleRows,
    state.cursor,
    (r: AssetRow) =>
      r.type === "domain-header" || r.type === "publisher-header" || r.type === "origin-header",
  );
  if (idx >= 0) state.cursor = idx;
}

/**
 * Toggle the info region of the domain the cursor is in — pressed via `[d]`.
 * Works whether the cursor sits on the `domain-header`, its `domain-info` line,
 * or any asset under it. No-op outside a domain.
 */
function toggleDomainInfoUnderCursor(): void {
  const row = state.visibleRows[state.cursor];
  if (!row) return;
  const origin = rowOrigin(row);
  let key: string | null = null;
  if (row.type === "domain-header" || row.type === "domain-info") {
    key = domainKey(origin, row.source!, row.domainSlug ?? NO_DOMAIN);
  } else if (row.type === "asset" && row.underDomain) {
    key = domainKey(origin, rowPublisher(row), rowDomain(row));
  }
  if (!key) {
    state.message = pc.dim("No domain here — [d] opens a domain's info region.");
    return;
  }
  if (state.expandedDomainInfo.has(key)) state.expandedDomainInfo.delete(key);
  else state.expandedDomainInfo.add(key);
  rebuildVisible();
}

// ── Collapse / expand / select (sources tab) ─────────────────────────────────

/**
 * Header-targeted +/-/space on the Sources tab. Org and repo headers
 * bulk-install their subtree; a domain header (and the root / domain-info) is
 * navigational — "handled" so +/- do nothing, but never bulk-installs.
 */
function actOnSourceHeader(row: AssetRow, action: Action): boolean {
  if (row.type === "org-header") {
    bulkSelection(sourceOrgRefs(state.sourceTreeRows, row.org!), action);
    return true;
  }
  if (row.type === "repo-header") {
    bulkSelection(sourceRepoRefs(state.sourceTreeRows, row.org!, row.source!), action);
    return true;
  }
  return row.type === "source-root" || row.type === "domain-header" || row.type === "domain-info";
}

/**
 * Apply a +/-/space selection to the Sources-tab row under the cursor. Headers
 * bulk-select via {@link actOnSourceHeader}; an asset leaf toggles its own
 * pending state (pressing the same key again clears it, mirroring the Assets tab).
 */
function selectSourceUnderCursor(action: Action): void {
  const row = state.visibleSourceRows[state.cursor];
  if (!row) return;
  if (actOnSourceHeader(row, action === "toggle" ? "toggle" : action)) return;
  const ref = assetRefOf(row);
  if (!ref) return;
  if (action === "toggle") {
    setSelection(ref, "toggle");
  } else if (action === "add") {
    setSelection(ref, state.pendingAdds.has(ref) ? "clear" : "add");
  } else {
    setSelection(ref, state.pendingRemoves.has(ref) ? "clear" : "remove");
  }
}

function expandSourceUnderCursor(): void {
  const row = state.visibleSourceRows[state.cursor];
  if (!row) return;
  if (row.type === "org-header" && state.collapsedSourceOrgs.has(row.org!)) {
    state.collapsedSourceOrgs.delete(row.org!);
    rebuildVisibleSources();
  } else if (row.type === "repo-header") {
    const k = repoKey(row.org!, row.source!);
    if (state.collapsedSourceRepos.has(k)) {
      state.collapsedSourceRepos.delete(k);
      rebuildVisibleSources();
    }
  } else if (row.type === "domain-header") {
    const k = srcDomainKey(row.org!, row.source!, row.domainSlug ?? NO_DOMAIN);
    if (state.collapsedSourceDomains.has(k)) {
      state.collapsedSourceDomains.delete(k);
      rebuildVisibleSources();
    }
  }
}

function collapseOrJumpSourceUnderCursor(): void {
  const row = state.visibleSourceRows[state.cursor];
  if (!row) return;

  if (row.type === "org-header") {
    if (!state.collapsedSourceOrgs.has(row.org!)) {
      state.collapsedSourceOrgs.add(row.org!);
      rebuildVisibleSources();
    }
    return;
  }

  if (row.type === "repo-header") {
    const k = repoKey(row.org!, row.source!);
    if (!state.collapsedSourceRepos.has(k)) {
      state.collapsedSourceRepos.add(k);
      rebuildVisibleSources();
    } else {
      const idx = findLastIndex(
        state.visibleSourceRows,
        state.cursor,
        (r: AssetRow) => r.type === "org-header",
      );
      if (idx >= 0) state.cursor = idx;
    }
    return;
  }

  if (row.type === "domain-header") {
    const k = srcDomainKey(row.org!, row.source!, row.domainSlug ?? NO_DOMAIN);
    if (!state.collapsedSourceDomains.has(k)) {
      state.collapsedSourceDomains.add(k);
      rebuildVisibleSources();
    } else {
      const idx = findLastIndex(
        state.visibleSourceRows,
        state.cursor,
        (r: AssetRow) => r.type === "repo-header",
      );
      if (idx >= 0) state.cursor = idx;
    }
    return;
  }

  // asset / domain-info / root → jump to nearest header above.
  const idx = findLastIndex(
    state.visibleSourceRows,
    state.cursor,
    (r: AssetRow) =>
      r.type === "domain-header" || r.type === "repo-header" || r.type === "org-header",
  );
  if (idx >= 0) state.cursor = idx;
}

/** Toggle the info region of the source-tree domain the cursor is in (`[d]`). */
function toggleSourceDomainInfoUnderCursor(): void {
  const row = state.visibleSourceRows[state.cursor];
  if (!row) return;
  let key: string | null = null;
  if (row.type === "domain-header" || row.type === "domain-info") {
    key = srcDomainKey(row.org!, row.source!, row.domainSlug ?? NO_DOMAIN);
  } else if (row.type === "asset" && row.underDomain) {
    key = srcDomainKey(rowOrg(row), rowGroup(row), rowDomain(row));
  }
  if (!key) {
    state.message = pc.dim("No domain here — [d] opens a domain's info region.");
    return;
  }
  if (state.expandedSourceDomainInfo.has(key)) state.expandedSourceDomainInfo.delete(key);
  else state.expandedSourceDomainInfo.add(key);
  rebuildVisibleSources();
}

/**
 * Find the last index in `arr` strictly before `endExclusive` matching `pred`.
 * Replacement for `Array.prototype.findLastIndex`, which requires ES2023 lib.
 */
function findLastIndex<T>(arr: T[], endExclusive: number, pred: (item: T) => boolean): number {
  for (let i = Math.min(endExclusive, arr.length) - 1; i >= 0; i--) {
    if (pred(arr[i] as T)) return i;
  }
  return -1;
}

// ── Keymap ───────────────────────────────────────────────────────────────────
//
// Declarative key bindings. Each binding lists the literal keys it matches, an
// optional tab predicate, and the handler. Handlers may return "break" to exit
// the main loop or "skip-render" to suppress the post-render call (used when
// the handler renders its own output, e.g. modal prompts).

// `void` is intentional here: handlers may also return Promise<void> from async
// functions that don't care about the dispatch sentinel. Treating "no return"
// as a third state is the simplest contract.
// biome-ignore lint/suspicious/noConfusingVoidType: see comment above
type KeyResult = "break" | "skip-render" | void;

interface KeyBinding {
  keys: string[];
  when?: (tab: Tab) => boolean;
  run: (key: string) => Promise<KeyResult> | KeyResult;
}

const TAB_ASSETS = (t: Tab) => t === "assets";
const TAB_SOURCES = (t: Tab) => t === "sources";
const TAB_LIBRARIES = (t: Tab) => t === "libraries";

const KEYMAP: KeyBinding[] = [
  // Quit (with pending check) — handled here, not via a modal subroutine, so
  // the main for-await loop owns key delivery (data listeners don't fire while
  // the stream is owned by an async iterator).
  {
    keys: ["\x03", "q", "\x1b"],
    run: () => {
      if (state.pendingAdds.size > 0 || state.pendingRemoves.size > 0) {
        state.awaitingExitConfirm = true;
        renderExitPrompt();
        return "skip-render";
      }
      return "break";
    },
  },
  { keys: ["\t"], run: () => void nextTab() },
  {
    keys: ["\x1b[A", "k"],
    run: async () => {
      moveCursor(-1);
      await previewSoftDepsUnderCursor();
    },
  },
  {
    keys: ["\x1b[B", "j"],
    run: async () => {
      moveCursor(1);
      await previewSoftDepsUnderCursor();
    },
  },

  // Collapse / expand — assets tab
  { keys: ["\x1b[C"], when: TAB_ASSETS, run: expandUnderCursor },
  { keys: ["\x1b[D"], when: TAB_ASSETS, run: collapseOrJumpUnderCursor },

  // Collapse / expand — sources tab
  { keys: ["\x1b[C"], when: TAB_SOURCES, run: expandSourceUnderCursor },
  { keys: ["\x1b[D"], when: TAB_SOURCES, run: collapseOrJumpSourceUnderCursor },

  // + : assets → bulk-add or single-add toggle
  {
    keys: ["+"],
    when: TAB_ASSETS,
    run: async () => {
      const row = state.visibleRows[state.cursor];
      if (!row) return;
      if (!actOnHeader(row, "add")) {
        const ref = assetRefOf(row);
        if (ref) setSelection(ref, state.pendingAdds.has(ref) ? "clear" : "add");
      }
      await previewSoftDepsUnderCursor();
    },
  },

  // Sources tab: + / - / space select (asset leaf or org/repo bulk header).
  { keys: ["+"], when: TAB_SOURCES, run: () => selectSourceUnderCursor("add") },
  { keys: ["-"], when: TAB_SOURCES, run: () => selectSourceUnderCursor("remove") },
  { keys: [" "], when: TAB_SOURCES, run: () => selectSourceUnderCursor("toggle") },
  { keys: ["a", "\r", "\n"], when: TAB_SOURCES, run: applyChanges },
  { keys: ["i"], when: TAB_SOURCES, run: showInfo },
  { keys: ["d"], when: TAB_SOURCES, run: toggleSourceDomainInfoUnderCursor },

  // Libraries tab: +/-/n/r/x/d/enter map to shell hints in the minimal scaffold.
  {
    keys: ["+", "n", "a"],
    when: TAB_LIBRARIES,
    run: () => libraryShellHint("Adding a library"),
  },
  {
    keys: ["-", "r", "x"],
    when: TAB_LIBRARIES,
    run: () => libraryShellHint("Removing a library"),
  },
  {
    keys: ["d"],
    when: TAB_LIBRARIES,
    run: () => libraryShellHint("Setting the default library"),
  },
  { keys: ["\r", "\n"], when: TAB_LIBRARIES, run: () => libraryShellHint("Library actions") },

  // - : assets → bulk-remove or single-remove toggle
  {
    keys: ["-"],
    when: TAB_ASSETS,
    run: async () => {
      const row = state.visibleRows[state.cursor];
      if (!row) return;
      if (!actOnHeader(row, "remove")) {
        const ref = assetRefOf(row);
        if (ref) setSelection(ref, state.pendingRemoves.has(ref) ? "clear" : "remove");
      }
      await previewSoftDepsUnderCursor();
    },
  },

  // Space — auto-toggle (single asset or bulk header)
  {
    keys: [" "],
    when: TAB_ASSETS,
    run: () => {
      const row = state.visibleRows[state.cursor];
      if (!row) return;
      if (!actOnHeader(row, "toggle")) {
        const ref = assetRefOf(row);
        if (ref) setSelection(ref, "toggle");
      }
    },
  },

  // s — sync all (any tab) — minimal scaffold delegates to shell.
  { keys: ["s"], run: syncAll },

  // a : assets → apply
  { keys: ["a"], when: TAB_ASSETS, run: applyChanges },

  // Enter — apply on assets tab
  { keys: ["\r", "\n"], when: TAB_ASSETS, run: applyChanges },

  // Info — assets only
  { keys: ["i"], when: TAB_ASSETS, run: showInfo },

  // Domain info region — assets only. Toggles the ⓘ line truncated ↔ full.
  { keys: ["d"], when: TAB_ASSETS, run: toggleDomainInfoUnderCursor },

  // Origin filter — assets only. 1/2/3/4 isolate one origin, 0 clears.
  { keys: ["1"], when: TAB_ASSETS, run: () => setOriginFilter("library") },
  { keys: ["2"], when: TAB_ASSETS, run: () => setOriginFilter("source") },
  { keys: ["3"], when: TAB_ASSETS, run: () => setOriginFilter("factory") },
  { keys: ["4"], when: TAB_ASSETS, run: () => setOriginFilter("store") },
  { keys: ["0"], when: TAB_ASSETS, run: () => setOriginFilter(null) },
];

async function dispatchKey(key: string): Promise<KeyResult> {
  for (const b of KEYMAP) {
    if (!b.keys.includes(key)) continue;
    if (b.when && !b.when(state.tab)) continue;
    return await b.run(key);
  }
  // Unrecognised key — no-op, no re-render.
  return "skip-render";
}

/** Handle the response to the exit-confirmation prompt. */
async function handleExitConfirm(key: string): Promise<KeyResult> {
  state.awaitingExitConfirm = false;

  if (key === "\x03" || key === "n" || key === "N") return "break";
  if (key === "y" || key === "Y") {
    await applyChanges();
    return "break";
  }
  if (key === "c" || key === "C" || key === "\x1b" || key === "\r" || key === "\n") {
    return; // re-render
  }
  // Unknown key: re-arm, redraw the prompt
  state.awaitingExitConfirm = true;
  renderExitPrompt();
  return "skip-render";
}

// ── Main loop ────────────────────────────────────────────────────────────────

async function run(projectDir: string): Promise<void> {
  am = new AssetManager({ projectDir });

  // The legacy auto-sync-on-open behaviour was tied to the `.skaile/repos/`
  // clone cache and was removed on 2026-05-12. Source sync now flows through
  // the shell-level `skaile source sync` command.

  await loadAssets();
  await loadSourcesAndLibraries();

  hideCursor();
  process.stdin.setRawMode(true);
  process.stdin.resume();

  render();

  for await (const chunk of process.stdin) {
    const key = chunk.toString();

    const result = state.awaitingExitConfirm
      ? await handleExitConfirm(key)
      : await dispatchKey(key);

    if (result === "break") break;
    if (result === "skip-render") continue;
    render();
  }

  showCursor();
  process.stdin.setRawMode(false);
  process.stdin.pause();
  clearScreen();
}

// ── Command ──────────────────────────────────────────────────────────────────

/**
 * Creates the `skaile manage` command.
 *
 * Opens an interactive terminal TUI (five tabs: Assets, Sources, Libraries,
 * Pending, Sync) for browsing, installing, and removing assets. The Sources tab
 * renders registered github sources as a `Sources → org → repo → domain → asset`
 * tree (domains default collapsed) and supports the same install/remove flow as
 * Assets. CRUD for sources and libraries remains in the shell
 * (`skaile source …` / `skaile library …`); the Libraries tab is still a
 * read-only scaffold pending AF-LIB-TUI-RICH.
 *
 * @returns Configured {@link Command} ready for `program.addCommand()`.
 * @docLink cli/commands/manage#make-manage-command
 */
export function makeManageCommand(): Command {
  return new Command("manage")
    .description("Interactive TUI for managing assets, sources, and libraries")
    .option("--project-dir <path>", "Project directory", process.cwd())
    .action(async (opts) => {
      await run(path.resolve(opts.projectDir));
    });
}
